// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { DistributionListGridModel } from '../../../state_manager/data_models/distribution_list_grid_model.ts'
import { FilterValue, SortConfig } from '../../../state_manager/interfaces/distribution_lists.ts'
import { fetchDistributionListsFiltered } from '../services/distributionListsService.ts'

export const DEFAULT_LISTS_SORTING: SortConfig[] = [
  { key: 'distribution_lists__name', direction: 'asc' }
]

/**
 * useDistributionLists — the Distribution List Definition grid's own data RPC
 * (view__distribution_lists), NOT the cached globalConfigs copy: a post-write
 * refetch must reflect DB truth (re-aggregated member sets) without a backend
 * restart. Screen-local state, no Redux (decision §2.12e / §2.13e).
 *
 * The create/edit surface is now a full-form tab (architect choice); the
 * form fetches its OWN detail + filter-options (cold-load — §2.13d), so this
 * grid hook no longer preloads the option universes.
 */
export const useDistributionLists = () => {
  const [listsData, setListsData] = useState<DistributionListGridModel[]>([])
  const [listsLoading, setListsLoading] = useState(false)
  const [activeSorting, setActiveSorting] = useState<SortConfig[]>(DEFAULT_LISTS_SORTING)
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>([])

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const handleFetchLists = useCallback(
    async (sorting: SortConfig[] = DEFAULT_LISTS_SORTING, filters: FilterValue[] = []) => {
      try {
        setListsLoading(true)
        const token = getAccessToken(store.getState().auth)
        const response = await fetchDistributionListsFiltered(
          sorting && sorting.length > 0 ? sorting : DEFAULT_LISTS_SORTING,
          filters ?? [],
          1,
          null,
          token
        )
        if (cancelledRef.current) return
        if (response.error) {
          if (process.env.NODE_ENV === 'development') console.error(response.error)
        }
        setListsData(response.data)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error(e)
      } finally {
        if (!cancelledRef.current) setListsLoading(false)
      }
    },
    []
  )

  // Initial fetch + refetch whenever filters / sorting change.
  useEffect(() => {
    const token = getAccessToken(store.getState().auth)
    if (token) handleFetchLists(activeSorting, activeFilters)
  }, [activeFilters, activeSorting, handleFetchLists])

  const handleSetSorting = useCallback((sorting: SortConfig[]) => {
    setActiveSorting(sorting)
  }, [])

  const handleSetFilters = useCallback((filters: FilterValue[]) => {
    setActiveFilters(filters)
  }, [])

  const refetch = useCallback(
    () => handleFetchLists(activeSorting, activeFilters),
    [handleFetchLists, activeSorting, activeFilters]
  )

  return {
    listsData,
    listsLoading,
    activeSorting,
    activeFilters,
    handleSetSorting,
    handleSetFilters,
    refetch
  }
}
